/**
 * Shared top bar for every screener surface (landing, question,
 * outcome). Renders the Greenbroad wordmark on the left, optional
 * context label + a persistent "Sign in →" link on the right when
 * the host supplies a click handler.
 *
 * The sign-in affordance is essential for returning users — without
 * it they'd land on `/` and be forced through the screener again
 * just to find the auth entry. Each screener page mounts this top
 * bar so the affordance is one click from any point in the flow.
 *
 * Routing is intentionally a CALLBACK prop rather than `useNavigate`
 * — keeps the component testable without a Router context, and
 * pushes the "where does sign-in live" decision up to the route
 * shell that already owns navigation (ScreenerEntry in AppRouter).
 */
import styles from './Screener.module.css';

export interface IScreenerTopBarProps {
  /**
   * Small contextual label rendered on the right side (e.g.,
   * "Marriage-based green card", "Pre-qualification screener"). Omit
   * to render only the wordmark + sign-in link.
   */
  metaLabel?: string;
  /**
   * Fires when the user clicks the persistent "Sign in →" link.
   * When omitted, the link is hidden — useful for tests + standalone
   * screener usage without an auth surface to navigate to.
   */
  onSignInClick?: () => void;
}

export function ScreenerTopBar({
  metaLabel,
  onSignInClick,
}: IScreenerTopBarProps): JSX.Element {
  return (
    <div className={styles.screener__topbar}>
      <img
        src="/brand/greenbroad-wordmark.png"
        alt="Greenbroad"
        className={styles.screener__topbar_brand}
        width={140}
        height={29}
      />
      <div className={styles.screener__topbar_right}>
        {metaLabel ? (
          <span className={styles.screener__topbar_meta}>{metaLabel}</span>
        ) : null}
        {onSignInClick ? (
          <button
            type="button"
            className={styles.screener__topbar_signin}
            onClick={onSignInClick}
            data-screener-signin
          >
            Already a member? <span aria-hidden="true">·</span>{' '}
            <strong>Sign in →</strong>
          </button>
        ) : null}
      </div>
    </div>
  );
}
